// Payouts — vendor payout queue.
//
// The cron-driven payouts-aggregator inserts rows in 'requested' status;
// operators move them through approve → mark-paid (or cancel). Bulk actions
// are intentionally absent — every payout is a money transfer and gets an
// audited per-row click.

import { useEffect, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  DataTable,
  type DataTableColumn,
  type RowKey,
} from '@drivecars/admin-ui';
import {
  KPI,
  PageHeader,
  ToolBtn,
  ToolChip,
  ToolSearch,
  Toolbar,
} from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  approvePayout,
  bulkPayoutAction,
  cancelPayout,
  downloadPayoutsCsv,
  fetchAdminPayouts,
  markPayoutPaid,
  type AdminPayoutRow,
  type AdminPayoutsListParams,
  type PayoutBulkAction,
  type PayoutStatus,
} from '@/lib/admin-api';
import { usePager } from '@/lib/use-pager';

type FilterKey = NonNullable<AdminPayoutsListParams['status']>;

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'requested', label: 'Requested' },
  { key: 'approved', label: 'Approved' },
  { key: 'paid', label: 'Paid' },
  { key: 'cancelled', label: 'Cancelled' },
];

const STATUS_KEYS: PayoutStatus[] = ['requested', 'approved', 'paid', 'cancelled'];

const fmtMoney = (v: number, currency: string) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency || 'EUR',
    maximumFractionDigits: 2,
  }).format(v);

const fmtPeriod = (from: string, to: string) => {
  const f = new Date(from).toLocaleDateString();
  const t = new Date(to).toLocaleDateString();
  return `${f} → ${t}`;
};

interface ActionCtx {
  busyId: number | null;
  onApprove: (row: AdminPayoutRow) => void;
  onMarkPaid: (row: AdminPayoutRow) => void;
  onCancel: (row: AdminPayoutRow) => void;
}

const buildColumns = (ctx: ActionCtx): DataTableColumn<AdminPayoutRow>[] => [
  {
    key: 'reference',
    h: 'Reference',
    cell: (r) => <span className="font-mono text-[12px]">{r.reference}</span>,
  },
  {
    key: 'vendor',
    h: 'Vendor',
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{r.vendor.name}</div>
        <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
          {r.vendor.email}
        </div>
      </div>
    ),
  },
  {
    key: 'period',
    h: 'Period',
    cell: (r) => (
      <span className="text-[11.5px] text-[color:var(--bo-mute)]">
        {fmtPeriod(r.periodFrom, r.periodTo)}
      </span>
    ),
  },
  {
    key: 'amount',
    h: 'Amount',
    right: true,
    cell: (r) => (
      <span className="tabular-nums font-semibold">
        {fmtMoney(r.amount, r.currency)}
      </span>
    ),
  },
  { key: 'status', h: 'Status', cell: (r) => <StatusPill status={r.status} /> },
  {
    key: 'paidAt',
    h: 'Paid at',
    right: true,
    cell: (r) => (
      <span className="tabular-nums text-[11.5px] text-[color:var(--bo-mute)]">
        {r.paidAt ? new Date(r.paidAt).toLocaleDateString() : '—'}
      </span>
    ),
  },
  {
    key: 'actions',
    h: 'Actions',
    cell: (r) => {
      const busy = ctx.busyId === r.id;
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          {r.status === 'requested' ? (
            <>
              <RowAction kind="primary" busy={busy} onClick={() => ctx.onApprove(r)}>
                Approve
              </RowAction>
              <RowAction kind="danger" busy={busy} onClick={() => ctx.onCancel(r)}>
                Cancel
              </RowAction>
            </>
          ) : null}
          {r.status === 'approved' ? (
            <>
              <RowAction kind="primary" busy={busy} onClick={() => ctx.onMarkPaid(r)}>
                Mark paid
              </RowAction>
              <RowAction kind="danger" busy={busy} onClick={() => ctx.onCancel(r)}>
                Cancel
              </RowAction>
            </>
          ) : null}
        </div>
      );
    },
  },
];

export function Payouts() {
  const [filter, setFilter] = useState<FilterKey>('requested');
  const [q, setQ] = useState('');
  const { page, setPage, limit, setLimit } = usePager();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<RowKey>>(new Set());
  const [bulkResult, setBulkResult] = useState<{
    succeeded: number;
    failed: number;
  } | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadPayoutsCsv({ status: filter, q: q.trim() || undefined });
    } catch (err) {
      setExportError((err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  // Any filter change lands back on page 1.
  useEffect(() => {
    setPage(1);
  }, [filter, q, setPage]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'payouts', { filter, q, page }],
    queryFn: ({ signal }) =>
      fetchAdminPayouts(
        { status: filter, q: q.trim() || undefined, page, limit },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'payouts'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'finance'] });
  };

  const approveMut = useMutation({ mutationFn: approvePayout, onSuccess: invalidate });
  const cancelMut = useMutation({
    mutationFn: ({ id, notes }: { id: number; notes?: string }) =>
      cancelPayout(id, { notes }),
    onSuccess: invalidate,
  });
  const paidMut = useMutation({
    mutationFn: ({ id, notes }: { id: number; notes?: string }) =>
      markPayoutPaid(id, { notes }),
    onSuccess: invalidate,
  });
  const bulkMut = useMutation({
    mutationFn: ({
      ids,
      action,
      notes,
    }: {
      ids: number[];
      action: PayoutBulkAction;
      notes?: string;
    }) => bulkPayoutAction(ids, action, notes),
    onSuccess: (res) => {
      setBulkResult({ succeeded: res.succeeded.length, failed: res.failed.length });
      setSelectedIds(new Set());
      invalidate();
    },
  });

  const runBulk = (action: PayoutBulkAction) => {
    const ids = Array.from(selectedIds, Number).filter(Number.isFinite);
    if (ids.length === 0) return;
    const verb =
      action === 'approve'
        ? 'Approve'
        : action === 'mark-paid'
          ? 'Mark as paid'
          : 'Cancel';
    if (!window.confirm(`${verb} ${ids.length} payout${ids.length === 1 ? '' : 's'}?`)) {
      return;
    }
    const promptLabel =
      action === 'mark-paid'
        ? 'Bank reference / note (optional):'
        : action === 'cancel'
          ? 'Cancellation reason (optional):'
          : null;
    const notes = promptLabel
      ? (window.prompt(promptLabel) ?? '').trim() || undefined
      : undefined;
    bulkMut.mutate({ ids, action, notes });
  };

  const busyId =
    (approveMut.isPending && (approveMut.variables as number)) ||
    (cancelMut.isPending && (cancelMut.variables as { id: number }).id) ||
    (paidMut.isPending && (paidMut.variables as { id: number }).id) ||
    null;

  const cols = buildColumns({
    busyId,
    onApprove: (r) => approveMut.mutate(r.id),
    onMarkPaid: (r) => {
      const notes = window.prompt('Bank reference / note (optional):') ?? undefined;
      paidMut.mutate({ id: r.id, notes: notes?.trim() || undefined });
    },
    onCancel: (r) => {
      const notes = window.prompt('Cancellation reason (optional):') ?? undefined;
      cancelMut.mutate({ id: r.id, notes: notes?.trim() || undefined });
    },
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const counts = data?.statusCounts ?? {};
  const totals = data?.statusTotals ?? {};
  const mutationError = approveMut.error || cancelMut.error || paidMut.error;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Finance"
        title="Payouts"
        sub={`${total.toLocaleString()} payouts · ${counts.requested ?? 0} requested · ${counts.approved ?? 0} approved.`}
        actions={
          <ToolBtn icon="download" onClick={() => void handleExport()}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </ToolBtn>
        }
      />
      {exportError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Export failed: {exportError}
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KPI
          label="Requested"
          value={fmtMoney(totals.requested ?? 0, 'EUR')}
          hint={`${counts.requested ?? 0} waiting approval`}
        />
        <KPI
          label="Approved"
          value={fmtMoney(totals.approved ?? 0, 'EUR')}
          hint={`${counts.approved ?? 0} waiting transfer`}
        />
        <KPI
          label="Paid"
          value={fmtMoney(totals.paid ?? 0, 'EUR')}
          hint={`${counts.paid ?? 0} settled`}
        />
        <KPI
          label="Cancelled"
          value={fmtMoney(totals.cancelled ?? 0, 'EUR')}
          hint={`${counts.cancelled ?? 0} void`}
        />
      </div>
      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load payouts: {(error as Error).message}
        </div>
      ) : null}
      {mutationError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Mutation failed: {(mutationError as Error).message}
        </div>
      ) : null}
      <Toolbar>
        <ToolSearch
          value={q}
          onChange={setQ}
          placeholder="Search reference, vendor email, business…"
        />
        {FILTERS.map((f) => (
          <ToolChip
            key={f.key}
            active={filter === f.key}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            {f.key !== 'all' && STATUS_KEYS.includes(f.key as PayoutStatus) ? (
              <span className="ms-1 tabular-nums text-[10.5px] text-[color:var(--bo-mute)]">
                {counts[f.key] ?? 0}
              </span>
            ) : null}
          </ToolChip>
        ))}
      </Toolbar>
      {bulkResult ? (
        <div
          role="status"
          className={`flex items-center justify-between rounded-lg border px-3 py-2 text-[12.5px] ${
            bulkResult.failed === 0
              ? 'border-[#a7f3d0] bg-[#ecfdf5] text-[#065f46]'
              : 'border-[#fde68a] bg-[#fef9c3] text-[#854d0e]'
          }`}
        >
          <span>
            Bulk action complete: {bulkResult.succeeded} succeeded
            {bulkResult.failed > 0 ? `, ${bulkResult.failed} failed` : ''}.
          </span>
          <button
            type="button"
            onClick={() => setBulkResult(null)}
            className="text-[11px] underline-offset-2 hover:underline"
          >
            dismiss
          </button>
        </div>
      ) : null}
      {selectedIds.size > 0 ? (
        <div className="sticky top-[60px] z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[color:var(--bo-line)] bg-white px-3 py-2 shadow-sm">
          <div className="text-[12.5px]">
            <strong>{selectedIds.size}</strong> selected
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="ms-3 text-[11.5px] text-[color:var(--bo-mute)] hover:underline"
            >
              clear
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <RowAction
              kind="primary"
              busy={bulkMut.isPending}
              onClick={() => runBulk('approve')}
            >
              Approve selected
            </RowAction>
            <RowAction
              kind="primary"
              busy={bulkMut.isPending}
              onClick={() => runBulk('mark-paid')}
            >
              Mark paid
            </RowAction>
            <RowAction
              kind="danger"
              busy={bulkMut.isPending}
              onClick={() => runBulk('cancel')}
            >
              Cancel selected
            </RowAction>
          </div>
        </div>
      ) : null}
      <DataTable
        columns={cols}
        rows={rows}
        emptyLabel={isLoading ? 'Loading…' : 'No matching payouts.'}
        onRowClick={(r) =>
          void navigate({ to: `/payouts/${r.id}` as never })
        }
        selection={{
          selectedIds,
          onToggle: (id) => {
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            });
          },
          onToggleAll: (ids) => {
            setSelectedIds((prev) => {
              const allHere = ids.every((id) => prev.has(id));
              const next = new Set(prev);
              if (allHere) {
                for (const id of ids) next.delete(id);
              } else {
                for (const id of ids) next.add(id);
              }
              return next;
            });
          },
        }}
        pagination={{ page, limit, total, onPageChange: setPage, onLimitChange: setLimit }}
      />
    </div>
  );
}
